import { API_HOST_URL } from './api.const';
import { getRequestHeaders, Opts, useAdminReactQuery } from './api.utils';
import { ToasterContext } from '../toaster/toaster.provider';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useContext } from 'react';
import { getAuthStore } from '../screens/auth/auth.utils';
import api from './api';
import type {
  AdminCouponCodesResponse,
  ApiUserMonitoring,
  BillingAdminResponse,
  CouponCode,
  Tariff,
} from './types';

export const useServerStatsQuery = () =>
  useQuery({
    queryKey: ['server-stats'],
    refetchInterval: 3000,
    queryFn: async () => {
      const store = getAuthStore();

      const [response] = await api(
        `Statistics/Users`,
        { method: 'GET', port: 6001 },
        {
          ...(store?.accessToken && {
            Authorization: `Bearer ${store.accessToken}`,
          }),
        },
      );

      return response.data as ApiUserMonitoring[];
    },
  });

export const useUsersQuery = (
  {
    search,
    type,
    offset,
    limit,
    onlineOnly,
  }: {
    search: string;
    offset: number;
    limit: number;
    onlineOnly: boolean;
    type: 'business' | 'standard';
  },
  opts?: Opts,
) =>
  useAdminReactQuery<{ users: any[]; total: number }>(
    `admin/users?offset=${offset}&onlineOnly=${onlineOnly}&limit=${limit}&type=${type}&search=${search}`,
  )(opts);
export const useAdminBillingQuery = ({ page }: { page: number }, opts?: Opts) =>
  useAdminReactQuery<BillingAdminResponse>(`admin/billing?page=${page}`)(opts);

export const useAdminOnlineUsersQuery = useAdminReactQuery<string[]>('admin/users/online');

export const userDetailsQuery = (userId: number, opts?: Opts) =>
  useAdminReactQuery<any>(`admin/users/${userId}`)(opts);

export const useAdminTariffsQuery = useAdminReactQuery<Tariff[]>('admin/tariffs');

export const useAdminCouponCodesQuery = ({ page = 1 }: { page?: number } = {}, opts?: Opts) =>
  useAdminReactQuery<AdminCouponCodesResponse>(`admin/coupons?page=${page}`)({
    queryKey: ['admin/coupons', page],
    ...opts,
  });

export const useAdminCouponDetailsQuery = (id: string | undefined, opts?: Opts) =>
  useAdminReactQuery<CouponCode>(`admin/coupons/${id}`)(opts);

// MUTATIONS

export const useUpdateUserExpiryDate = () => {
  const queryClient = useQueryClient();
  const headers = getRequestHeaders({ ignoreActingUser: true });
  const { error } = useContext(ToasterContext);

  return useMutation({
    onError: (err: any) => {
      error({ message: err.message });
    },
    onSuccess: (d, v) => {
      queryClient.invalidateQueries({ queryKey: [`admin/users/${v.id}`] });
    },
    mutationFn: async ({ id, validTo }: { id: string | number; validTo: Date }) => {
      const [response] = await api<{}>(
        `${API_HOST_URL}/admin/users/${id}/update-expiration`,
        { method: 'POST', body: { validTo } },
        headers,
      );

      return response;
    },
  });
};

export const useCreateCouponCode = () => {
  const headers = getRequestHeaders({ ignoreActingUser: true });
  const { error } = useContext(ToasterContext);

  return useMutation({
    onError: (err: any) => {
      error({ message: err.message });
    },
    mutationFn: async ({
      code,
      rules,
      tariffId,
      type,
      usagesLeft,
    }: {
      code: string | null;
      rules: any;
      type: 'tariff' | 'discount';
      tariffId: number | null;
      usagesLeft: number;
    }) => {
      const [response] = await api<CouponCode>(
        `${API_HOST_URL}/admin/coupons/generate`,
        { method: 'POST', body: { code, rules, tariffId, type, usagesLeft } },
        headers,
      );

      return response;
    },
  });
};

export const useUpdateCouponCode = () => {
  const queryClient = useQueryClient();
  const headers = getRequestHeaders({ ignoreActingUser: true });
  const { error } = useContext(ToasterContext);

  return useMutation({
    onError: (err: any) => {
      error({ message: err.message });
    },
    onSuccess: (_d, v: any) => {
      // v should contain { code }
      queryClient.invalidateQueries({ queryKey: ['admin/coupons'] });
      if (v?.code) queryClient.invalidateQueries({ queryKey: [`admin/coupons/${v.code}`] });
    },
    mutationFn: async ({ code, payload }: { code: string; payload: any }) => {
      const [response] = await api<CouponCode>(
        `${API_HOST_URL}/admin/coupons/${code}`,
        { method: 'PUT', body: payload },
        headers,
      );

      return response;
    },
  });
};

export const useRemoveCouponCode = () => {
  const queryClient = useQueryClient();
  const headers = getRequestHeaders({ ignoreActingUser: true });
  const { error } = useContext(ToasterContext);

  return useMutation({
    onError: (err: any) => {
      error({ message: err.message });
    },
    onSuccess: (_d, v: any) => {
      queryClient.invalidateQueries({ queryKey: ['admin/coupons'] });
    },
    mutationFn: async ({ id }: { id: string }) => {
      const [response] = await api<CouponCode>(
        `${API_HOST_URL}/admin/coupons/${id}`,
        { method: 'DELETE' },
        headers,
      );

      return response;
    },
  });
};
